// planosUtils.ts
import type { PlanPeriodicity, SingleValidityUnit } from './planoTypes';
import { urlFile } from '../../../AxiosConfig';

/** Monta a URL do arquivo a partir do ID */
export const buildImageUrl = (id?: string) => (id ? `${urlFile}/${id}` : '');

/** Formata unidade de tempo (suporta pt-BR e alguns termos em inglês) */
export const formatUnit = (value: number, unit: string) => {
    const normalized = unit.toLowerCase();

    switch (normalized) {
        case 'dia':
        case 'day':
        case 'days':
            return value > 1 ? 'dias' : 'dia';

        case 'semana':
        case 'semanas':
        case 'week':
            return value > 1 ? 'semanas' : 'semana';

        case 'quinzena':
        case 'quinzenas':
            return value > 1 ? 'quinzenas' : 'quinzena';

        case 'bimestre':
        case 'bimestres':
            return value > 1 ? 'bimestres' : 'bimestre';

        case 'trimestre':
        case 'trimestres':
            return value > 1 ? 'trimestres' : 'trimestre';

        case 'semestre':
        case 'semestres':
            return value > 1 ? 'semestres' : 'semestre';

        case 'mes':
        case 'mês':
        case 'meses':
        case 'month':
        case 'months':
            return value > 1 ? 'meses' : 'mês';

        case 'ano':
        case 'anos':
        case 'year':
        case 'years':
            return value > 1 ? 'anos' : 'ano';

        default:
            return unit;
    }
};

/** Formata recorrência de serviço */
export const formatServiceRecurrence = (rec?: string) => {
    if (!rec) return '';
    switch (rec) {
        case 'semanal':
            return 'por semana';
        case 'mensal':
            return 'por mês';
        case 'total':
            return 'no ciclo do plano';
        default:
            return rec;
    }
};

export const mapPeriodicityToRecurrence = (periodicity?: PlanPeriodicity) => {
    switch (periodicity) {
        case 'weekly':
            return { value: 1, unit: 'semana' as const };
        case 'biweekly':
            return { value: 2, unit: 'semana' as const };
        case 'monthly':
            return { value: 1, unit: 'mes' as const };
        case 'bimonthly':
            return { value: 2, unit: 'mes' as const };
        case 'quarterly':
            return { value: 3, unit: 'mes' as const };
        case 'biannual':
            return { value: 6, unit: 'mes' as const };
        case 'yearly':
            return { value: 1, unit: 'ano' as const };
        default:
            return null;
    }
};

export const formatPeriodicityLabel = (periodicity?: PlanPeriodicity) => {
    if (!periodicity) return '';
    switch (periodicity) {
        case 'single':
            return 'Única';
        case 'weekly':
            return 'Semanal';
        case 'biweekly':
            return 'Quinzenal';
        case 'monthly':
            return 'Mensal';
        case 'bimonthly':
            return 'Bimestral';
        case 'quarterly':
            return 'Trimestral';
        case 'biannual':
            return 'Semestral';
        case 'yearly':
            return 'Anual';
        default:
            return 'Personalizada';
    }
};

type PlanDurationInput = {
    periodicity?: PlanPeriodicity | null;
    quantity?: number;
    indeterminateQuantity?: boolean;
    singleValidityValue?: number;
    singleValidityUnit?: SingleValidityUnit;
};

export const getPlanDurationLabel = (plano: PlanDurationInput): string | null => {
    const periodicity = plano.periodicity;

    if (!periodicity) return null;

    if (periodicity === 'single') {
        const value = Number(plano.singleValidityValue ?? 0);
        const unitRaw = String(plano.singleValidityUnit ?? '').toLowerCase();
        const unitPt = unitRaw === 'day' ? 'dia' : unitRaw === 'month' ? 'mes' : '';

        if (!value || !unitPt) return null;
        const unitLabel = formatUnit(value, unitPt);
        return `${value} ${unitLabel}`;
    }

    if (plano.indeterminateQuantity) {
        return 'Indeterminado';
    }

    if (!plano.quantity || plano.quantity <= 0) return null;

    const rec = mapPeriodicityToRecurrence(periodicity);
    if (!rec) {
        return `${plano.quantity} cobranças`;
    }

    const totalValue = rec.value * plano.quantity;
    const unitLabel = formatUnit(totalValue, rec.unit);
    return `${totalValue} ${unitLabel}`;
};

export const formatPlanPrice = (value?: number) =>
    Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });

/** Sufixo do valor no formato "R$ 100,00/mês". Pagamento unico nao tem sufixo. */
export const getPlanPriceSuffix = (periodicity?: PlanPeriodicity | null) => {
    const rec = mapPeriodicityToRecurrence(periodicity ?? undefined);
    if (!rec) return '';

    const unitLabel = formatUnit(rec.value, rec.unit);
    return rec.value === 1 ? `/${unitLabel}` : `/${rec.value} ${unitLabel}`;
};

/** Resumo da cobranca, como "Mensal, 12 cobranças" ou "Pagamento único, válido por 6 meses". */
export const getPlanBillingLabel = (plano: PlanDurationInput) => {
    if (plano.periodicity === 'single') {
        const duration = getPlanDurationLabel(plano);
        return duration ? `Pagamento único, válido por ${duration}` : 'Pagamento único';
    }

    const periodicity = formatPeriodicityLabel(plano.periodicity ?? undefined);
    if (!periodicity) return 'Cobrança recorrente';
    if (plano.indeterminateQuantity) return `${periodicity}, até cancelar`;

    const quantity = Number(plano.quantity || 0);
    if (!quantity) return periodicity;
    return `${periodicity}, ${quantity} ${quantity === 1 ? 'cobrança' : 'cobranças'}`;
};

/** Imagem de capa: a do plano ou, na falta dela, a de um servico incluido. */
export const getPlanCoverUrl = (plano: {
    imagesIds?: string[];
    services?: Array<{ service?: { imagesIds?: string[]; principalImage?: string } }>;
}) => {
    const serviceImages = (plano.services || []).flatMap((item) => [
        ...(Array.isArray(item?.service?.imagesIds) ? item.service!.imagesIds! : []),
        ...(item?.service?.principalImage ? [item.service.principalImage] : []),
    ]);

    return buildImageUrl(plano.imagesIds?.[0] || serviceImages[0]);
};
